import Maybe from 'true-myth/maybe';
import { z } from 'zod';

import { CustomFieldPrefix } from './common';

export type CustomAttributeKey = `custom.${string}`;

type StringAttribute = {
    readonly allowedValues?: readonly string[] | undefined;
    readonly type: 'string';
    readonly value: string | null;
};

type BooleanAttribute = { readonly type: 'boolean'; readonly value: boolean | null };

type IntegerAttribute = { readonly type: 'integer'; readonly value: number | null };

type DateAttribute = { readonly type: 'date'; readonly value: string | null };

export const customAttributeTypeSchema = z.union([
    z.literal('string'),
    z.literal('boolean'),
    z.literal('integer'),
    z.literal('date'),
]);

export type CustomAttributeType = z.infer<typeof customAttributeTypeSchema>;

export const customAttributeOptionsSchema = z.object({
    allowedValues: z.string().array().optional(),
    name: z.string(),
    readOnly: z.boolean(),
    type: customAttributeTypeSchema,
});

export type CustomAttributeOptions = z.infer<typeof customAttributeOptionsSchema>;

export type CustomAttribute = {
    readonly allowedValues?: readonly string[] | undefined;
    readonly isReadonly: boolean;
    readonly key: CustomAttributeKey;
    readonly name: string;
    readonly type: CustomAttributeType;
};

export type CustomAttributeWithValue = CustomAttribute &
    (BooleanAttribute | DateAttribute | IntegerAttribute | StringAttribute);

export const customAttributesSchema = z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]));

export type CustomAttributes = z.infer<typeof customAttributesSchema>;

export const mapToAttributesWithValue = (
    attributes: Maybe<readonly CustomAttributeOptions[]>,
    values: Maybe<CustomAttributes>,
): readonly CustomAttributeWithValue[] => {
    // biome-ignore lint/suspicious/useIterableCallbackReturn: suppressed due to migration
    return attributes.unwrapOr([] as CustomAttributeOptions[]).map((attribute) => {
        const key: CustomAttributeKey = `${CustomFieldPrefix}${attribute.name}`;
        const value = Maybe.of(values.mapOr(null, (it) => it[attribute.name]));
        switch (attribute.type) {
            case 'string':
                return {
                    allowedValues: attribute.allowedValues,
                    isReadonly: attribute.readOnly,
                    key,
                    name: attribute.name,
                    type: attribute.type,
                    value: value.mapOr(null, (it) => it as string),
                };
            case 'boolean':
                return {
                    isReadonly: attribute.readOnly,
                    key,
                    name: attribute.name,
                    type: attribute.type,
                    value: value.mapOr(null, (it) => it as boolean),
                };
            case 'integer':
                return {
                    isReadonly: attribute.readOnly,
                    key,
                    name: attribute.name,
                    type: attribute.type,
                    value: value.mapOr(null, (it) => it as number),
                };
            case 'date':
                return {
                    isReadonly: attribute.readOnly,
                    key,
                    name: attribute.name,
                    type: attribute.type,
                    value: value.mapOr(null, (it) => it as string),
                };
        }
    });
};
